const PLUGIN_NAME = "NDGNavigation";

function runtimeHost(host) {
  if (host) return host;
  if (typeof globalThis !== "undefined") return globalThis;
  return null;
}

export function getNativeGoogleNavigationPlugin(host) {
  const root = runtimeHost(host);
  if (!root) return null;

  if (root.__NDG_NATIVE_NAVIGATION__) {
    return root.__NDG_NATIVE_NAVIGATION__;
  }

  const capacitor = root.Capacitor;
  if (!capacitor) return null;
  if (typeof capacitor.isNativePlatform === "function" && !capacitor.isNativePlatform()) {
    return null;
  }
  if (
    typeof capacitor.isPluginAvailable === "function"
    && !capacitor.isPluginAvailable(PLUGIN_NAME)
  ) {
    return null;
  }
  return capacitor.Plugins?.[PLUGIN_NAME] || null;
}

function coordinate(value, limit) {
  if (
    (typeof value !== "number" && typeof value !== "string")
    || (typeof value === "string" && !value.trim())
  ) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && Math.abs(parsed) <= limit ? parsed : null;
}

export function normalizeNativeNavigationWaypoints(raw) {
  if (!Array.isArray(raw) || raw.length === 0) {
    throw new Error("A complete NDG route with a return destination is required.");
  }
  // Reject the whole itinerary: dropping even one invalid stop changes the
  // server-owned teaching route. Array.from also exposes sparse-array holes.
  const normalized = Array.from(raw, (item, index) => {
    const latitude = coordinate(item?.latitude, 90);
    const longitude = coordinate(item?.longitude, 180);
    if (
      latitude === null || longitude === null
      || !["challenge", "return"].includes(item?.kind)
      || (item?.position != null && (
        !["number", "string"].includes(typeof item.position)
        || !Number.isInteger(Number(item.position))
        || Number(item.position) < 1
      ))
    ) {
      throw new Error(`NDG route stop ${index + 1} is invalid; navigation was not changed.`);
    }
    return {
      kind: item.kind,
      position: item.position == null ? null : Number(item.position),
      environmentId: item.environment_id || null,
      name: String(item.name || (item.kind === "return" ? "Return" : "Teaching challenge")),
      latitude,
      longitude,
      sequence: index + 1,
    };
  });
  if (
    normalized[normalized.length - 1].kind !== "return"
    || normalized.filter((item) => item.kind === "return").length !== 1
  ) {
    throw new Error("The NDG route must end with exactly one return destination.");
  }
  return normalized;
}

export function nativeGoogleNavigationAvailable(host) {
  return Boolean(getNativeGoogleNavigationPlugin(host));
}

async function requireNativeSDKReady(plugin) {
  // Older bridge implementations have no capability probe; their commands
  // retain responsibility for reporting readiness. The iOS host supplies it.
  if (typeof plugin?.getCapabilities !== "function") return;
  const capabilities = await plugin.getCapabilities();
  if (capabilities?.sdkReady !== true) {
    const error = new Error(
      capabilities?.reason || "Google navigation is not ready in this build.",
    );
    error.code = "NDG_NAVIGATION_SDK_UNAVAILABLE";
    throw error;
  }
}

export async function startNativeGoogleNavigation(plugin, {
  lessonEventId,
  waypoints,
  muted = false,
} = {}) {
  if (!plugin?.start) throw new Error("Native Google navigation is unavailable.");
  const normalized = normalizeNativeNavigationWaypoints(waypoints);
  if (typeof lessonEventId !== "string" || !lessonEventId.trim()) {
    throw new Error("A prepared NDG teaching route is required before native navigation.");
  }
  await requireNativeSDKReady(plugin);
  return plugin.start({
    lessonEventId,
    waypoints: normalized,
    muted: Boolean(muted),
  });
}

export async function updateNativeGoogleNavigation(plugin, {
  lessonEventId,
  waypoints,
} = {}) {
  if (!plugin?.updateRoute) throw new Error("Native Google route updates are unavailable.");
  const normalized = normalizeNativeNavigationWaypoints(waypoints);
  if (typeof lessonEventId !== "string" || !lessonEventId.trim()) {
    throw new Error("A server-approved NDG route is required before updating native navigation.");
  }
  await requireNativeSDKReady(plugin);
  return plugin.updateRoute({
    lessonEventId,
    waypoints: normalized,
  });
}

export async function stopNativeGoogleNavigation(plugin) {
  if (!plugin?.stop) return;
  const receipt = await plugin.stop();
  if (receipt === false) throw new Error("Google did not confirm navigation stopped");
}

export async function setNativeGoogleMuted(plugin, muted) {
  if (!plugin?.setMuted) return;
  await requireNativeSDKReady(plugin);
  await plugin.setMuted({ muted: Boolean(muted) });
}

export async function addNativeGoogleListener(plugin, eventName, callback) {
  if (!plugin?.addListener) return null;
  const handle = await plugin.addListener(eventName, callback);
  return handle || null;
}

export async function removeNativeGoogleListener(handle) {
  if (!handle?.remove) return;
  await handle.remove();
}
